import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, map, Observable, Subject } from 'rxjs';

/**
 * Emits whenever the session ends, regardless of which code path triggered it.
 *
 * Any service that caches user-scoped data (PII, decrypted account ids,
 * beneficiary lists, transfer state, etc.) should inject this and clear its
 * cache via `cleared$`. That keeps sensitive data out of memory between
 * sessions on a shared device.
 *
 * Triggers on the following OAuth events:
 * - `logout` — user-initiated logout (or any code that called `OAuthService.logOut()`)
 * - `session_terminated` — IdP closed the session
 * - `session_error`, `token_refresh_error`, `token_error`, `code_error` —
 *   any terminal auth failure that the rest of the app treats as logout.
 */
@Injectable({ providedIn: 'root' })
export class SessionTeardownService {
  private readonly cleared = new Subject<void>();

  /**
   * Hot observable. Subscribers should pipe `takeUntilDestroyed()` or
   * `takeUntil(destroy$)`; emissions are fire-and-forget after that.
   */
  readonly cleared$: Observable<void> = this.cleared.asObservable();

  constructor(private readonly oAuthService: OAuthService) {
    this.oAuthService.events
      .pipe(
        filter((event) => SessionTeardownService.TEARDOWN_EVENTS.has(event.type)),
        map(() => undefined as void),
      )
      .subscribe(() => this.cleared.next());
  }

  private static readonly TEARDOWN_EVENTS = new Set<string>([
    'logout',
    'session_terminated',
    'session_error',
    'token_refresh_error',
    'token_error',
    'code_error',
  ]);
}
